import React, { useRef, useState } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { candidateIntakeParse, createCandidate } from "@/lib/api";
import {
  UploadCloud, FileText, Loader2, Sparkles, X, Plus, ArrowLeft,
  UserPlus, AlertTriangle,
} from "lucide-react";

const inputCls = "bg-[#0A0F1E] border-white/10 h-10 text-white";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const AVAILABILITY = ["Immediate", "2 Weeks", "30 Days", "Currently Employed", "Not Looking"];
const ACCEPT = ".pdf,.doc,.docx";

const emptyForm = {
  first_name: "", last_name: "", email: "", phone: "", current_position: "",
  skills: [], industries: [], years_experience: "", availability: "", ai_summary: "",
};

const Field = ({ label, children, required }) => (
  <div className="space-y-1.5">
    <Label className="text-white/60 text-xs">{label}{required && <span className="text-[#FF5C7A]"> *</span>}</Label>
    {children}
  </div>
);

// Recruiter-facing Candidate Intake. Two stages: (1) choose — upload a resume
// (default) or create manually; (2) review — edit the AI-extracted fields, then
// save. Uses the SAME backend parsing + candidate pipeline as the Careers page.
export const CandidateIntakeModal = ({ token, onClose, onCreated }) => {
  const [stage, setStage] = useState("choose"); // choose | review
  const [mode, setMode] = useState("resume");   // resume | manual
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [dup, setDup] = useState(null);
  const [resumeMeta, setResumeMeta] = useState(null);
  const [parsedRaw, setParsedRaw] = useState({});
  const [aiRaw, setAiRaw] = useState({});
  const [form, setForm] = useState(emptyForm);
  const [skillInput, setSkillInput] = useState("");
  const fileRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFile = async (file) => {
    if (!file) return;
    const okExt = /\.(pdf|docx?|doc)$/i.test(file.name);
    if (!okExt) { toast.error("Please upload a PDF, DOC, or DOCX file"); return; }
    if (file.size > 12 * 1024 * 1024) { toast.error("Resume exceeds the 12MB limit"); return; }
    setParsing(true);
    try {
      const res = await candidateIntakeParse(token, file);
      const f = res.fields || {};
      setResumeMeta({
        resume_file_id: res.resume_file_id,
        resume_filename: res.resume_filename,
        resume_fingerprint: res.resume_fingerprint,
      });
      setParsedRaw(res.parsed || {});
      setAiRaw(res.ai || {});
      setDup(res.duplicate || null);
      setForm({
        first_name: f.first_name || "", last_name: f.last_name || "",
        email: f.email || "", phone: f.phone || "",
        current_position: f.current_position || "",
        skills: f.skills || [], industries: f.industries || [],
        years_experience: f.years_experience ? String(f.years_experience) : "",
        availability: f.availability || "",
        ai_summary: (res.ai || {}).professional_summary || "",
      });
      setMode("resume");
      setStage("review");
      toast.success("Resume parsed \u2014 review the details below");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not parse the resume");
    } finally {
      setParsing(false);
    }
  };

  const startManual = () => {
    setMode("manual");
    setResumeMeta(null);
    setParsedRaw({});
    setAiRaw({});
    setDup(null);
    setForm(emptyForm);
    setStage("review");
  };

  const addSkill = () => {
    const t = skillInput.trim();
    if (!t) return;
    if (!form.skills.includes(t)) set("skills", [...form.skills, t]);
    setSkillInput("");
  };
  const removeSkill = (s) => set("skills", form.skills.filter((x) => x !== s));

  const save = async () => {
    if (!form.first_name.trim() || !form.last_name.trim() || !EMAIL_RE.test(form.email.trim())) {
      toast.error("First name, last name and a valid email are required");
      return;
    }
    setSaving(true);
    try {
      const summary = form.ai_summary.trim();
      const ai = summary || (aiRaw && Object.keys(aiRaw).length)
        ? { ...(aiRaw || {}), professional_summary: summary }
        : null;
      const body = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        current_position: form.current_position.trim() || null,
        skills: form.skills,
        industries: form.industries || [],
        years_experience: form.years_experience === "" ? null : Number(form.years_experience),
        availability: form.availability || null,
        parsed: parsedRaw || {},
        ...(ai ? { ai } : {}),
        ...(resumeMeta || {}),
      };
      const res = await createCandidate(token, body);
      toast.success("Candidate created");
      onCreated(res.candidate.id);
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error("A candidate with this email, phone, or resume already exists");
      } else {
        toast.error(e?.response?.data?.detail || "Failed to create candidate");
      }
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !saving && !parsing) onClose(); }}>
      <DialogContent className="bg-[#0E1424] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="candidate-intake-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <UserPlus size={18} className="text-[#66B2FF]" /> New Candidate
          </DialogTitle>
        </DialogHeader>

        {stage === "choose" ? (
          <div className="py-2">
            {/* Drag & drop resume */}
            <div
              role="button"
              tabIndex={0}
              onClick={() => !parsing && fileRef.current?.click()}
              onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !parsing) { e.preventDefault(); fileRef.current?.click(); } }}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0]); }}
              data-testid="candidate-intake-dropzone"
              className={`rounded-xl border-2 border-dashed p-10 text-center cursor-pointer transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#66B2FF] ${dragOver ? "border-[#66B2FF] bg-[#66B2FF]/10" : "border-white/15 hover:border-white/30 hover:bg-white/5"}`}
            >
              {parsing ? (
                <div className="flex flex-col items-center gap-3 text-white/70">
                  <Loader2 size={30} className="animate-spin text-[#66B2FF]" />
                  <div className="font-medium">Parsing resume with AI…</div>
                  <div className="text-white/40 text-sm">Extracting name, skills, experience and a summary</div>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <div className="h-14 w-14 rounded-full bg-[#1E6BFF]/15 flex items-center justify-center">
                    <UploadCloud size={26} className="text-[#66B2FF]" />
                  </div>
                  <div className="text-white font-medium mt-1">Drag &amp; drop a resume, or <span className="text-[#66B2FF]">Browse</span></div>
                  <div className="text-white/40 text-sm">PDF, DOC or DOCX &middot; up to 12MB</div>
                </div>
              )}
              <input ref={fileRef} type="file" accept={ACCEPT} className="hidden"
                data-testid="candidate-intake-file-input"
                onChange={(e) => handleFile(e.target.files?.[0])} />
            </div>

            <div className="flex items-center gap-3 my-4">
              <div className="h-px flex-1 bg-white/10" />
              <span className="text-white/35 text-xs uppercase tracking-wide">or</span>
              <div className="h-px flex-1 bg-white/10" />
            </div>

            <Button variant="outline" className="w-full border-white/15 bg-transparent hover:bg-white/5 text-white h-11"
              onClick={startManual} disabled={parsing} data-testid="candidate-intake-manual">
              <Plus size={16} className="mr-2" /> Create manually (no resume)
            </Button>
          </div>
        ) : (
          <div className="py-1 space-y-4">
            {mode === "resume" && resumeMeta?.resume_filename && (
              <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-[#0A0F1E] px-3 py-2 text-sm text-white/70" data-testid="candidate-intake-resume-chip">
                <FileText size={15} className="text-[#66B2FF]" />
                <span className="truncate">{resumeMeta.resume_filename}</span>
                <span className="ml-auto text-[#2EE59D] text-xs flex items-center gap-1"><Sparkles size={12} /> AI parsed</span>
              </div>
            )}

            {dup && (
              <div className="flex items-start gap-2 rounded-lg border border-[#F5B94A]/30 bg-[#F5B94A]/10 px-3 py-2 text-sm text-[#F5B94A]" data-testid="candidate-intake-dup-warning">
                <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                <span>A similar candidate already exists{dup.name ? ` (${dup.name})` : ""}. Saving with the same email or resume will be blocked — edit the email to add anyway.</span>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="First Name" required>
                <Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)}
                  className={inputCls} placeholder="Jane" data-testid="candidate-intake-first" />
              </Field>
              <Field label="Last Name" required>
                <Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)}
                  className={inputCls} placeholder="Doe" data-testid="candidate-intake-last" />
              </Field>
              <Field label="Email" required>
                <Input value={form.email} onChange={(e) => set("email", e.target.value)} type="email"
                  className={inputCls} placeholder="jane@example.com" data-testid="candidate-intake-email" />
              </Field>
              <Field label="Phone">
                <Input value={form.phone} onChange={(e) => set("phone", e.target.value)}
                  className={inputCls} placeholder="+1 555 000 0000" data-testid="candidate-intake-phone" />
              </Field>
              <Field label="Primary Role">
                <Input value={form.current_position} onChange={(e) => set("current_position", e.target.value)}
                  className={inputCls} placeholder="Customer Service Representative" data-testid="candidate-intake-role" />
              </Field>
              <Field label="Experience (years)">
                <Input value={form.years_experience} onChange={(e) => set("years_experience", e.target.value.replace(/[^0-9]/g, ""))}
                  type="number" min="0" className={inputCls} placeholder="5" data-testid="candidate-intake-experience" />
              </Field>
              <Field label="Availability">
                <Select value={form.availability || undefined} onValueChange={(v) => set("availability", v)}>
                  <SelectTrigger className={inputCls} data-testid="candidate-intake-availability"><SelectValue placeholder="Select availability" /></SelectTrigger>
                  <SelectContent className="bg-[#0E1424] border-white/10 text-white">
                    {AVAILABILITY.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <Field label="Skills">
              <div className="flex flex-wrap gap-1.5 mb-2">
                {form.skills.map((s) => (
                  <span key={s} className="text-xs bg-[#1E6BFF]/15 text-[#66B2FF] rounded-md px-2 py-1 flex items-center gap-1" data-testid="candidate-intake-skill">
                    {s}<button type="button" onClick={() => removeSkill(s)} aria-label={`Remove ${s}`}><X size={11} /></button>
                  </span>
                ))}
                {form.skills.length === 0 && <span className="text-white/40 text-sm">No skills yet</span>}
              </div>
              <div className="flex gap-2">
                <Input value={skillInput} onChange={(e) => setSkillInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addSkill(); } }}
                  className={inputCls} placeholder="Add a skill and press Enter" data-testid="candidate-intake-skill-input" />
                <Button type="button" onClick={addSkill} className="bg-[#1E6BFF] hover:bg-[#3B82F6] h-10"><Plus size={15} /></Button>
              </div>
            </Field>

            <Field label="AI Summary">
              <Textarea value={form.ai_summary} onChange={(e) => set("ai_summary", e.target.value)}
                rows={4} className="bg-[#0A0F1E] border-white/10 text-white resize-none"
                placeholder={mode === "manual" ? "Optional \u2014 a summary will be generated on save if left blank." : "AI-generated summary (editable)"}
                data-testid="candidate-intake-summary" />
            </Field>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          {stage === "review" ? (
            <>
              <Button variant="ghost" className="mr-auto text-white/60 hover:text-white"
                onClick={() => setStage("choose")} disabled={saving} data-testid="candidate-intake-back">
                <ArrowLeft size={15} className="mr-1" /> Back
              </Button>
              <Button variant="ghost" className="text-white/60 hover:text-white" onClick={onClose} disabled={saving}>Cancel</Button>
              <Button className="bg-[#1E6BFF] hover:bg-[#1E6BFF]/90" onClick={save} disabled={saving} data-testid="candidate-intake-save">
                {saving ? <Loader2 className="animate-spin mr-1" size={15} /> : <UserPlus size={15} className="mr-1" />}
                Save Candidate
              </Button>
            </>
          ) : (
            <Button variant="ghost" className="text-white/60 hover:text-white" onClick={onClose} disabled={parsing} data-testid="candidate-intake-cancel">Cancel</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CandidateIntakeModal;
